/**
 * EMBEDDING SERVICE - Servicio de generación de embeddings
 * 
 * Genera embeddings vectoriales usando Vertex AI.
 * Los embeddings se usan para búsqueda semántica y RAG.
 * 
 * ARQUITECTURA:
 * - Frontend llama a este servicio
 * - Este servicio llama a Cloud Function (evita CORS)
 * - Cloud Function usa Vertex AI text-embedding-004
 * 
 * USO:
 * ```ts
 * const embedding = await embeddingService.generateEmbedding("texto a vectorizar");
 * logger.info(embedding.embedding); // [0.123, -0.456, ...]
 * ```
 */

import { functions } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import {
    EmbeddingRequest,
    EmbeddingResult,
    BatchEmbeddingRequest,
    BatchEmbeddingResult,
    EmbeddingModel,
    DEFAULT_EMBEDDING_MODEL,
    EMBEDDING_CONFIG,
} from './types';

const logger = Logger.getLogger('EmbeddingService');

// ============================================
// CACHE LOCAL (Evita llamadas duplicadas)
// ============================================

interface CacheEntry {
    embedding: number[];
    timestamp: number;
}

const CACHE_TTL_MS = 1000 * 60 * 60; // 1 hora
const cache = new Map<string, CacheEntry>();

function getCacheKey(text: string, model: EmbeddingModel): string {
    // Hash simple del texto (primeros 100 chars + length)
    const textKey = `${text.substring(0, 100)}_${text.length}`;
    return `${model}:${textKey}`;
}

function getFromCache(text: string, model: EmbeddingModel): number[] | null {
    const key = getCacheKey(text, model);
    const entry = cache.get(key);

    if (!entry) return null;

    // Verificar TTL
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        cache.delete(key);
        return null;
    }

    return entry.embedding;
}

function setCache(text: string, model: EmbeddingModel, embedding: number[]): void {
    const key = getCacheKey(text, model);
    cache.set(key, { embedding, timestamp: Date.now() });

    // Limitar tamaño del cache (max 1000 entradas)
    if (cache.size > 1000) {
        const firstKey = cache.keys().next().value;
        if (firstKey) cache.delete(firstKey);
    }
}

// ============================================
// EMBEDDING SERVICE
// ============================================

class EmbeddingService {
    private readonly defaultModel: EmbeddingModel = DEFAULT_EMBEDDING_MODEL;

    /**
     * Generar embedding para un texto
     */
    async generateEmbedding(request: EmbeddingRequest | string): Promise<EmbeddingResult> {
        const text = typeof request === 'string' ? request : request.text;
        const model = typeof request === 'string' ? this.defaultModel : (request.model || this.defaultModel);

        // Validar texto
        if (!text || text.trim().length === 0) {
            throw new Error('El texto no puede estar vacío');
        }

        // Truncar si excede límite de tokens (aprox 4 chars por token)
        const maxChars = EMBEDDING_CONFIG[model].maxTokens * 4;
        const truncatedText = text.length > maxChars
            ? text.substring(0, maxChars)
            : text;

        // Verificar cache
        const cached = getFromCache(truncatedText, model);
        if (cached) {
            logger.debug('Embedding from cache', { textLength: truncatedText.length });
            return {
                embedding: cached,
                model,
                dimensions: EMBEDDING_CONFIG[model].dimensions,
            };
        }

        try {
            logger.debug('Generating embedding', {
                textLength: truncatedText.length,
                model
            });

            // Llamar a Cloud Function
            const generateEmbedding = functions.httpsCallable('generateEmbedding');
            const result = await generateEmbedding({
                text: truncatedText,
                model,
            });

            const embedding = result.data.embedding as number[];
            const tokensUsed = result.data.tokensUsed as number | undefined;

            // Validar resultado
            if (!embedding || !Array.isArray(embedding)) {
                throw new Error('Invalid embedding response from Cloud Function');
            }

            // Guardar en cache
            setCache(truncatedText, model, embedding);

            logger.info('Embedding generated', {
                dimensions: embedding.length,
                tokensUsed,
                cached: false,
            });

            return {
                embedding,
                model,
                dimensions: embedding.length,
                tokensUsed,
            };

        } catch (error: any) {
            logger.error('Failed to generate embedding', error, {
                textLength: text.length
            });
            throw error;
        }
    }

    /**
     * Generar embeddings en batch (más eficiente que múltiples llamadas)
     */
    async generateBatchEmbeddings(request: BatchEmbeddingRequest): Promise<BatchEmbeddingResult> {
        const { texts, model = this.defaultModel } = request;

        if (!texts || texts.length === 0) {
            return {
                embeddings: [],
                model,
                totalTokens: 0,
            };
        }

        // Para batches pequeños, procesar en paralelo
        if (texts.length <= 5) {
            const results = await Promise.all(
                texts.map(text => this.generateEmbedding({ text, model }))
            );

            return {
                embeddings: results.map(r => r.embedding),
                model,
                totalTokens: results.reduce((sum, r) => sum + (r.tokensUsed || 0), 0),
            };
        }

        try {
            logger.info('Generating batch embeddings', {
                count: texts.length,
                model
            });

            // Para batches grandes, usar Cloud Function de batch
            const generateBatchEmbeddings = functions.httpsCallable('generateBatchEmbeddings');
            const result = await generateBatchEmbeddings({
                texts,
                model,
            });

            return {
                embeddings: result.data.embeddings,
                model,
                totalTokens: result.data.totalTokens || 0,
            };

        } catch (error: any) {
            logger.error('Failed to generate batch embeddings', error, {
                count: texts.length,
            });
            throw error;
        }
    }

    /**
     * Calcular similitud coseno entre dos embeddings
     */
    cosineSimilarity(a: number[], b: number[]): number {
        if (a.length !== b.length) {
            throw new Error('Embeddings must have same dimensions');
        }

        let dotProduct = 0;
        let normA = 0;
        let normB = 0;

        for (let i = 0; i < a.length; i++) {
            dotProduct += a[i] * b[i];
            normA += a[i] * a[i];
            normB += b[i] * b[i];
        }

        const magnitude = Math.sqrt(normA) * Math.sqrt(normB);

        return magnitude === 0 ? 0 : dotProduct / magnitude;
    }

    /**
     * Encontrar los K documentos más similares a un query
     */
    findMostSimilar(
        queryEmbedding: number[],
        documents: Array<{ id: string; embedding: number[] }>,
        k: number = 10
    ): Array<{ id: string; similarity: number }> {
        const scored = documents.map(doc => ({
            id: doc.id,
            similarity: this.cosineSimilarity(queryEmbedding, doc.embedding),
        }));

        return scored
            .sort((a, b) => b.similarity - a.similarity)
            .slice(0, k);
    }

    /**
     * Limpiar cache local
     */
    clearCache(): void {
        cache.clear();
        logger.debug('Embedding cache cleared');
    }

    /**
     * Obtener estadísticas del cache
     */
    getCacheStats(): { size: number; maxSize: number } {
        return {
            size: cache.size,
            maxSize: 1000,
        };
    }
}

// Singleton export
export const embeddingService = new EmbeddingService();
export default embeddingService;
